/**
 * Image Utilities
 *
 * Shared utilities for optimizing images across the app.
 * Uses Supabase image transforms for WebP conversion and resizing.
 */

/**
 * Check if URL is a Supabase storage URL
 */
export function isSupabaseStorageUrl(url: string): boolean {
  return url.includes('.supabase.co/storage/v1/');
}

/**
 * Check if URL points to a YouTube thumbnail CDN host.
 */
export function isYouTubeThumbnailUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === 'i.ytimg.com' || parsed.hostname.endsWith('.ytimg.com');
  } catch {
    return false;
  }
}

const YOUTUBE_THUMBNAIL_VARIANTS = new Set([
  'default',
  'mqdefault',
  'hqdefault',
  'sddefault',
  'maxresdefault',
  'hq720',
]);

function extractYouTubeVideoIdFromYtimgPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const viIndex = segments.findIndex((segment) => segment === 'vi' || segment === 'vi_webp');
  if (viIndex === -1) return null;
  return segments[viIndex + 1] || null;
}

function extractYouTubeThumbnailVariant(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const viIndex = segments.findIndex((segment) => segment === 'vi' || segment === 'vi_webp');
  if (viIndex === -1) return 'hqdefault';

  const rawVariant = segments[viIndex + 2] || '';
  const normalized = rawVariant.replace(/\.(jpg|jpeg|png|webp)$/i, '').toLowerCase();

  if (!normalized) return 'hqdefault';
  return YOUTUBE_THUMBNAIL_VARIANTS.has(normalized) ? normalized : 'hqdefault';
}

/**
 * Extract YouTube video ID from watch, short, embed, or thumbnail URLs.
 */
export function extractYouTubeVideoId(input: string | null | undefined): string | null {
  if (!input) return null;

  // If a direct 11-char YouTube ID is passed.
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }

  try {
    const parsed = new URL(input);
    const host = parsed.hostname.toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id || null;
    }

    if (host.includes('youtube.com')) {
      const v = parsed.searchParams.get('v');
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

      const segments = parsed.pathname.split('/').filter(Boolean);
      const pathId =
        segments[0] === 'embed' || segments[0] === 'shorts' || segments[0] === 'live'
          ? segments[1]
          : null;

      if (pathId && /^[a-zA-Z0-9_-]{11}$/.test(pathId)) return pathId;
    }

    if (host.includes('ytimg.com')) {
      const id = extractYouTubeVideoIdFromYtimgPath(parsed.pathname);
      if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) return id;
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Convert a YouTube thumbnail URL into a WebP URL.
 */
export function getYouTubeThumbnailWebpUrl(
  url: string | null | undefined,
  fallbackVideoId?: string | null,
  fallbackVariant: string = 'hqdefault'
): string {
  const videoId = extractYouTubeVideoId(url) || extractYouTubeVideoId(fallbackVideoId) || '';
  if (!videoId) return url || '';

  let variant = fallbackVariant;

  if (url) {
    try {
      const parsed = new URL(url);
      variant = extractYouTubeThumbnailVariant(parsed.pathname);
    } catch {
      variant = fallbackVariant;
    }
  }

  return `https://i.ytimg.com/vi_webp/${videoId}/${variant}.webp`;
}

export type AspectRatio = '1:1' | '16:9' | '4:3';

interface OptimizedUrlOptions {
  /** Width in pixels (default: 280) */
  width?: number;
  /** Aspect ratio for height calculation (default: '1:1') */
  aspectRatio?: AspectRatio;
  /** Quality 1-100 (default: 80) */
  quality?: number;
}

/**
 * Transform Supabase URL to request optimized image with proper sizing.
 * 
 * Features:
 * - Preserves aspect ratio using resize=contain
 * - WebP automatically served to supporting browsers
 * - Configurable width, height, and quality
 * 
 * @param url - Original image URL
 * @param options - Optimization options
 * @returns Optimized URL (or original if not Supabase)
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  options: OptimizedUrlOptions = {}
): string {
  if (!url) return '';

  if (isYouTubeThumbnailUrl(url)) {
    return getYouTubeThumbnailWebpUrl(url);
  }

  if (!isSupabaseStorageUrl(url)) {
    return url;
  }

  const { width = 280, aspectRatio = '1:1', quality = 80 } = options;

  // Strip existing query params
  const baseUrl = url.split('?')[0];
  
  // Convert to render endpoint for transforms
  const transformUrl = baseUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  // Calculate height based on aspect ratio
  let height: number;
  switch (aspectRatio) {
    case '16:9':
      height = Math.round(width * 9 / 16);
      break;
    case '4:3':
      height = Math.round(width * 3 / 4);
      break;
    case '1:1':
    default:
      height = width;
  }

  // Build params - right-sized transforms for faster transfers.
  // Supabase render endpoint determines output format; forcing format=webp
  // causes 400s in our current project config.
  const params = new URLSearchParams();
  params.set('width', width.toString());
  params.set('height', height.toString());
  params.set('resize', 'contain');
  params.set('quality', quality.toString());

  return `${transformUrl}?${params.toString()}`;
}

/**
 * Generate srcset for responsive images
 */
export function generateImageSrcSet(
  url: string | null | undefined,
  options: OptimizedUrlOptions = {}
): string {
  if (!url || !isSupabaseStorageUrl(url)) {
    return '';
  }

  const { width = 280, aspectRatio = '1:1' } = options;

  const widths = [
    Math.round(width * 0.5),
    Math.round(width * 0.75),
    width,
    Math.round(width * 1.5),
    Math.round(width * 2),
  ].filter(w => w >= 50 && w <= 2500);

  return widths
    .map(w => `${getOptimizedImageUrl(url, { ...options, width: w, aspectRatio })} ${w}w`)
    .join(', ');
}

/**
 * Responsive srcset for event thumbnails.
 * - YouTube thumbnails: use native WebP variants
 * - Supabase/custom: use existing image transform pipeline
 */
export function generateEventThumbnailSrcSet(
  url: string | null | undefined,
  youtubeVideoId?: string | null,
  options: OptimizedUrlOptions = {}
): string {
  const resolvedYouTubeId = extractYouTubeVideoId(url) || extractYouTubeVideoId(youtubeVideoId);
  if (resolvedYouTubeId) {
    const requestedWidth = options.width ?? 480;

    if (requestedWidth >= 1000) {
      return [
        `https://i.ytimg.com/vi_webp/${resolvedYouTubeId}/hqdefault.webp 480w`,
        `https://i.ytimg.com/vi_webp/${resolvedYouTubeId}/hq720.webp 720w`,
        `https://i.ytimg.com/vi_webp/${resolvedYouTubeId}/maxresdefault.webp 1280w`,
      ].join(', ');
    }

    return [
      `https://i.ytimg.com/vi_webp/${resolvedYouTubeId}/mqdefault.webp 320w`,
      `https://i.ytimg.com/vi_webp/${resolvedYouTubeId}/hqdefault.webp 480w`,
      `https://i.ytimg.com/vi_webp/${resolvedYouTubeId}/hq720.webp 720w`,
    ].join(', ');
  }

  return generateImageSrcSet(url, options);
}

/**
 * Preset for hero images - optimized for full-width hero sections
 * Uses 16:9 aspect ratio and higher quality for prominence
 */
export function getHeroImageUrl(url: string | null | undefined): string {
  return getOptimizedImageUrl(url, {
    width: 1200, // Aggressively smaller for LCP wins
    aspectRatio: '16:9',
    quality: 70, // Lower quality for faster load
  });
}

/**
 * Preset for event hero images.
 * Includes YouTube fallback when we only have a video ID.
 */
export function getEventHeroImageUrl(
  url: string | null | undefined,
  youtubeVideoId?: string | null
): string {
  const resolvedYouTubeId = extractYouTubeVideoId(url) || extractYouTubeVideoId(youtubeVideoId);
  if (resolvedYouTubeId) {
    return getYouTubeThumbnailWebpUrl(url, resolvedYouTubeId, 'hq720');
  }

  return getHeroImageUrl(url);
}

/**
 * Preset for card thumbnails - balanced size and quality
 */
export function getCardThumbnailUrl(url: string | null | undefined): string {
  return getOptimizedImageUrl(url, {
    width: 480, // Smaller cards for faster load
    aspectRatio: '16:9',
    quality: 70,
  });
}

/**
 * Preset for event card thumbnails.
 * Includes YouTube fallback when we only have a video ID.
 */
export function getEventCardThumbnailUrl(
  url: string | null | undefined,
  youtubeVideoId?: string | null
): string {
  const resolvedYouTubeId = extractYouTubeVideoId(url) || extractYouTubeVideoId(youtubeVideoId);
  if (resolvedYouTubeId) {
    return getYouTubeThumbnailWebpUrl(url, resolvedYouTubeId, 'hqdefault');
  }

  return getCardThumbnailUrl(url);
}

/**
 * Preset for mascot images - larger square format for decorative elements
 * Used in collection cards, host displays, etc.
 */
export function getMascotImageUrl(url: string | null | undefined): string {
  return getOptimizedImageUrl(url, {
    width: 320, // Smaller decorative images
    aspectRatio: '1:1',
    quality: 75, // Balanced quality for faster load
  });
}

/**
 * Preset for avatar images - small circular profile pictures
 */
export function getAvatarUrl(url: string | null | undefined): string {
  return getOptimizedImageUrl(url, {
    width: 96, // Smaller avatars
    aspectRatio: '1:1',
    quality: 75,
  });
}
